import { useCallback, useEffect, useState } from 'react'
import { X, Edit2, Trash2, RefreshCw } from 'lucide-react'
import { Card, Button, Input, Select, Textarea, SearchBar, ConfirmDialog, Modal } from '../../../shared/components/UI'
import { getPartyName, getRows } from '../../../shared/lib/partyType'

const emptyForm = { name: '', contact: '', email: '', gstNumber: '', address: '', status: 'active' }

function formatCurrency(value) {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '-'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount)
}

function getContactValue(item) {
  return item?.contact || item?.mobile || item?.phone || ''
}

function getGstValue(item) {
  return item?.gst_number || item?.gstNumber || item?.gst || ''
}

function getStatusValue(item) {
  return item?.status || 'active'
}

function toFormState(item) {
  return {
    name: getPartyName(item),
    contact: getContactValue(item),
    email: item?.email || '',
    gstNumber: getGstValue(item),
    address: item?.address || '',
    status: getStatusValue(item),
  }
}

export default function EntityDirectoryPage({ api, entity }) {
  const singular = entity?.singular || 'Item'
  const plural = entity?.plural || 'Items'
  const searchPlaceholder = entity?.searchPlaceholder || `Search ${plural.toLowerCase()}...`
  const emailPlaceholder = entity?.emailPlaceholder || `${singular.toLowerCase()}@email.com`
  const exampleName = entity?.exampleName || ''
  const totalLabel = entity?.totalLabel || 'Total Purchases'
  const dateLabel = entity?.dateLabel || 'Last Purchase'

  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [records, setRecords] = useState([])
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [deletingId, setDeletingId] = useState(null)

  const loadRecords = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.list()
      setRecords(getRows(res))
      setError('')
    } catch (err) {
      setRecords([])
      setError(err?.response?.data?.message || `Unable to load ${plural.toLowerCase()}.`)
    } finally {
      setLoading(false)
    }
  }, [api, plural])

  useEffect(() => {
    loadRecords()
  }, [loadRecords])

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }))

  const startNew = () => {
    setEditing('new')
    setForm(emptyForm)
    setError('')
  }

  const startEdit = async (item) => {
    setEditing(item.id)
    setForm(emptyForm)
    setSaving(true)
    try {
      const res = await api.byId(item.id)
      const payload = res?.data?.data || res?.data || item
      setForm(toFormState(payload))
      setError('')
    } catch (err) {
      setEditing(null)
      setError(err?.response?.data?.message || `Unable to load ${singular.toLowerCase()} details.`)
    } finally {
      setSaving(false)
    }
  }

  const cancel = () => {
    setEditing(null)
    setForm(emptyForm)
    setError('')
  }

  const save = async () => {
    const payload = {
      name: form.name.trim(),
      contact: form.contact.trim(),
      email: form.email.trim(),
      gstNumber: form.gstNumber.trim(),
      address: form.address.trim(),
      status: form.status,
    }

    if (!payload.name || !payload.contact) {
      setError(`${singular} name and contact are required.`)
      return
    }

    setSaving(true)
    try {
      if (editing === 'new') {
        await api.create(payload)
      } else {
        await api.update(editing, payload)
      }
      cancel()
      await loadRecords()
    } catch (err) {
      setError(err?.response?.data?.message || `Unable to save ${singular.toLowerCase()}.`)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingId) return
    setSaving(true)
    try {
      if (api.delete) {
        await api.delete(deletingId)
      } else if (api.deleteVendor) {
        await api.deleteVendor(deletingId)
      } else if (api.deleteCustomer) {
        await api.deleteCustomer(deletingId)
      } else {
        throw new Error('API does not support delete.')
      }
      setDeletingId(null)
      await loadRecords()
    } catch (err) {
      setError(err?.response?.data?.message || `Unable to delete ${singular.toLowerCase()}.`)
    } finally {
      setSaving(false)
    }
  }

  const visible = records.filter((item) => {
    const query = search.toLowerCase()
    return (
      getPartyName(item).toLowerCase().includes(query) ||
      getContactValue(item).toLowerCase().includes(query) ||
      getGstValue(item).toLowerCase().includes(query)
    )
  })

  const isDirty = form.name.trim() !== '' || form.contact.trim() !== ''

  if (loading) return <div className="p-8 text-center text-gray-400">Loading {plural.toLowerCase()}...</div>

  return (
    <div className="space-y-4">
      <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder={searchPlaceholder}>
        <Button onClick={startNew}>+ Add {singular}</Button>
        <button onClick={loadRecords} className="p-2 text-gray-400 hover:text-brand-600 transition-colors" title="Refresh">
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </SearchBar>

      <Card bodyClass="!p-0 overflow-hidden ring-1 ring-gray-100/50 rounded-2xl shadow-sm">
        <table className="data-table">
          <thead>
            <tr className="bg-gray-50/50">
              <th>{singular} Name</th>
              <th>Contact</th>
              <th>GST</th>
              <th>{totalLabel}</th>
              <th>{dateLabel}</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length > 0 ? visible.map((item) => (
              <tr key={item.id} className="group hover:bg-gray-50/50 transition-colors">
                <td>
                   <span className="font-bold text-gray-800 tracking-tight">{getPartyName(item) || '-'}</span>
                </td>
                <td className="td-mono text-xs font-bold text-gray-600">{getContactValue(item) || '-'}</td>
                <td className={getGstValue(item) ? 'td-mono text-xs' : 'text-gray-300 text-[10px]'}>{getGstValue(item) || '-'}</td>
                <td className="font-black text-brand-700 text-sm">
                  {formatCurrency(item.totalPurchases || item.totalSales || 0)}
                </td>
                <td className="text-gray-400 text-[10px] font-bold uppercase tracking-tighter">
                  {item.lastPurchase || item.lastDispatch || '-'}
                </td>
                <td>
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full border ${
                    getStatusValue(item) === 'active' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-gray-100 text-gray-400 border-gray-100'
                  }`}>
                    {getStatusValue(item)}
                  </span>
                </td>
                <td className="pr-4">
                  <div className="flex gap-1 justify-end">
                    <button 
                      onClick={() => startEdit(item)}
                      className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button 
                      onClick={() => setDeletingId(item.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="7" className="text-center text-sm text-gray-400 py-20 italic">No {plural.toLowerCase()} found in directory.</td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {editing && (
        <Modal
          isOpen={!!editing}
          onClose={cancel}
          title={editing === 'new' ? `Add New ${singular}` : `Edit ${singular}`}
          size="md"
          confirmClose={isDirty}
        >
          <div className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-xs font-bold rounded-xl animate-in fade-in zoom-in duration-200">
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label={`${singular} name`} value={form.name} onChange={f('name')} placeholder={exampleName ? `e.g. ${exampleName}` : ''} className="rounded-xl" />
              <Input label="Contact number" value={form.contact} onChange={f('contact')} placeholder="Mobile number" type="tel" className="rounded-xl" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Email Address" value={form.email} onChange={f('email')} placeholder={emailPlaceholder} type="email" className="rounded-xl" />
              <Input label="GST Number" value={form.gstNumber} onChange={f('gstNumber')} placeholder="15-digit GST code" className="rounded-xl font-mono" />
            </div>

            <Textarea label="Full Address" value={form.address} onChange={f('address')} placeholder="Enter business address..." className="rounded-xl min-h-[100px]" />
            
            <Select
              label="Account Status"
              value={form.status}
              onChange={f('status')}
              options={[
                { label: 'Active - Fully Operational', value: 'active' },
                { label: 'Inactive - Account Suspended', value: 'inactive' },
              ]}
              className="rounded-xl"
            />

            <div className="pt-4">
              <Button onClick={save} disabled={saving} className="w-full h-14 rounded-2xl font-bold shadow-lg shadow-brand-100 bg-brand-600 text-white">
                {saving ? 'Saving...' : `Save ${singular}`}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        isOpen={!!deletingId}
        onClose={() => { setDeletingId(null); setError(''); }}
        onConfirm={handleDelete}
        title={`Delete ${singular}?`}
        message={`Are you sure you want to permanently remove this ${singular.toLowerCase()}? This action cannot be undone.`}
        confirmText="Yes, Delete"
        isLoading={saving}
        error={error}
        variant="danger"
      />
    </div>
  )
}
